import { Box, Button, TextField, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IAlertDetail } from '~/utils/interface/alert';

export type UnprocessedStatusProps = {
  data: IAlertDetail;
  onRefresh: (context: { note: string }) => void | Promise<void>;
};

const UnprocessedStatus = (props: UnprocessedStatusProps) => {
  const { data, onRefresh } = props;
  const { t } = useTranslation('admin/alert');
  const [note, setNote] = useState(data.note ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');

  useEffect(() => {
    setNote(data.note ?? '');
    setNoteError('');
  }, [data.id, data.note]);

  const handleProcess = async () => {
    const trimmedNote = note.trim();
    if (!trimmedNote) {
      setNoteError(t('alertDetail.noteRequired'));
      return;
    }

    setSubmitting(true);
    try {
      await Promise.resolve(onRefresh({ note: trimmedNote }));
    } finally {
      setSubmitting(false);
    }
  };

  const labelCell = (text: string) => (
    <Box className='flex h-5 w-[130px] shrink-0 items-center overflow-hidden opacity-100'>
      <Typography component='span' className='truncate text-sm leading-[140%] text-[#7B8794] font-["Manrope",sans-serif]'>
        {text}
      </Typography>
    </Box>
  );

  return (
    <Box className='flex w-full flex-col gap-[10px]'>
      <Box className='flex w-full min-w-0 flex-row items-center gap-[30px]'>
        {labelCell(t('alertDetail.statusField'))}
        <Typography component='span' className='min-w-0 flex-1 text-left text-[14px] font-medium leading-[140%] tracking-[0.02em] text-orange-500 font-["Manrope",sans-serif]'>
          {t('alertDetail.unprocessedState')}
        </Typography>
      </Box>

      <Box className='flex w-full min-w-0 flex-row items-start gap-[30px]'>
        {labelCell(t('alertDetail.noteField'))}
        <TextField
          multiline
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (noteError && e.target.value.trim()) setNoteError('');
          }}
          placeholder={t('alertDetail.notePlaceholder')}
          disabled={submitting}
          variant='outlined'
          error={Boolean(noteError)}
          sx={{
            width: 316,
            maxWidth: '100%',
            minWidth: 0,
            flexShrink: 1,
            '& .MuiOutlinedInput-root': {
              minHeight: 80,
              maxHeight: 80,
              height: 80,
              alignItems: 'flex-start',
              borderRadius: '12px',
              padding: '8px',
              boxSizing: 'border-box',
              gap: '10px',
              overflow: 'hidden',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D7DEE7',
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D7DEE7',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D7DEE7',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D32F2F',
            },
            '& .MuiInputBase-input': {
              padding: 0,
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '140%',
              color: '#161A1F',
              maxHeight: 'calc(80px - 16px) !important',
              overflowY: 'auto !important',
              overflowX: 'hidden',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              resize: 'none',
              boxSizing: 'border-box',
            },
          }}
        />
      </Box>
      {noteError && (
        <Typography component='span' className='ml-[160px] pl-[30px] text-[14px] font-normal leading-[140%] tracking-[0.02em] text-[#D32F2F]'>
          {noteError}
        </Typography>
      )}

      <Button
        type='button'
        variant='contained'
        disableElevation
        disabled={submitting}
        onClick={() => void handleProcess()}
        className='w-full rounded-xl bg-[#123B6D] py-3 font-semibold text-white normal-case hover:bg-[#0E2F58]'
      >
        {t('alertDetail.processButton')}
      </Button>
    </Box>
  );
};

export default memo(UnprocessedStatus);
